import { debounce, DebouncedFunc } from 'lodash';
import { useCallback, useRef } from 'react';

const DEFAULT_DELAY = 100;

/**
 * Debounces a function call. Returns a stable debounced function that always
 * invokes the latest version of the callback without recreating the debounce
 * timer. Does not cause re-renders on its own.
 *
 * Returns a lodash DebouncedFunc, so callers can `.flush()` the pending call
 * (e.g. on blur/save before validation) or `.cancel()` it.
 *
 * Use this when you need to throttle imperative calls (e.g., saving, API requests,
 * validation) triggered by user input. For debouncing a reactive value to use in
 * a useEffect dependency, use useDebounce instead.
 *
 * @example
 * const debouncedSave = useDebounceCallback((text: string) => {
 *   saveToServer(text);
 * }, 500);
 *
 * <Input onChange={({ detail }) => debouncedSave(detail.value)} onBlur={() => debouncedSave.flush()} />
 */
const useDebounceCallback = (
  callback: (...args: any[]) => void,
  delay = DEFAULT_DELAY
): DebouncedFunc<(...args: any[]) => void> => {
  const callbackRef = useRef<(...args: any[]) => void>();
  callbackRef.current = callback;

  return useCallback(
    debounce((...args) => {
      if (callbackRef.current) {
        callbackRef.current(...args);
      }
    }, delay),
    []
  );
};

export default useDebounceCallback;
